import {
    AutocompleteInput,
    Create,
    DateInput,
    NumberInput,
    ReferenceInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, ListChecks, Receipt } from 'lucide-react';
import { useDataProvider } from 'ra-core';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { AutoReferenceField } from '../documents/shared/AutoReferenceField';
import { VendorBillItemsInput } from './VendorBillItemsInput';
import { VendorBillTotalsWatcher } from './VendorBillTotalsWatcher';
import { StatusSelectInput } from '../documents/shared/StatusSelectInput';

// When a GRN is selected, auto-fetch its vendor, linked PO and (via PO -> SO)
// the originating quotation, so the bill can't be linked to unrelated docs.
// See bug tracker VB #10.
const GRNLinker = () => {
    const grnId = useWatch({ name: 'grn_id' });
    const { setValue } = useFormContext();
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (!grnId) return;
        let cancelled = false;

        (async () => {
            try {
                const { data: grn } = await dataProvider.getOne('goods_receipts', { id: grnId });
                if (cancelled || !grn) return;
                if (grn.vendor_id) setValue('vendor_id', grn.vendor_id, { shouldDirty: true });
                if (grn.po_id) {
                    setValue('po_id', grn.po_id, { shouldDirty: true });

                    // PO -> Sales Order -> Quotation
                    const { data: po } = await dataProvider.getOne('purchase_orders', { id: grn.po_id });
                    if (cancelled) return;
                    if (po?.sales_order_id) {
                        const { data: so } = await dataProvider.getOne('sales_orders', { id: po.sales_order_id });
                        if (!cancelled && so?.quotation_id) {
                            setValue('quotation_id', so.quotation_id, { shouldDirty: true });
                        }
                    }
                }
            } catch {
                /* best effort — leave fields for manual entry */
            }
        })();

        return () => { cancelled = true; };
    }, [grnId, dataProvider, setValue]);

    return null;
};

// PO dropdown scoped to the selected vendor so unrelated POs can't be picked.
const VendorScopedPOInput = ({ readOnly }: { readOnly?: boolean }) => {
    const vendorId = useWatch({ name: 'vendor_id' });
    return (
        <ReferenceInput
            source="po_id"
            reference="purchase_orders"
            perPage={100}
            sort={{ field: 'date', order: 'DESC' }}
            filter={vendorId ? { vendor_id: vendorId } : {}}
        >
            <AutocompleteInput optionText="reference" label="Purchase Order" readOnly={readOnly} />
        </ReferenceInput>
    );
};

export const VendorBillCreate = () => {
    const location = useLocation();
    const stateRecord = location.state?.record || {};

    return (
        <Create title="Record Vendor Bill" redirect="show" record={stateRecord}>
            <SimpleForm className="w-full space-y-6">
                <GRNLinker />
                <VendorBillTotalsWatcher />
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Bill Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AutoReferenceField docType="vendor_bill" />
                            <ReferenceInput source="vendor_id" reference="vendors" perPage={100}>
                                <AutocompleteInput
                                    optionText="name"
                                    isRequired
                                    readOnly={!!stateRecord.vendor_id}
                                    label="Vendor"
                                />
                            </ReferenceInput>

                            <DateInput source="bill_date" isRequired defaultValue={new Date().toISOString().split('T')[0]} />
                            <DateInput source="due_date" />

                            <ReferenceInput source="grn_id" reference="goods_receipts" perPage={100} sort={{ field: 'received_date', order: 'DESC' }}>
                                <AutocompleteInput optionText="reference" label="GRN" readOnly={!!stateRecord.grn_id} />
                            </ReferenceInput>

                            <VendorScopedPOInput readOnly={!!stateRecord.po_id} />

                            <ReferenceInput source="quotation_id" reference="quotations" perPage={100}>
                                <AutocompleteInput optionText="reference" label="Quotation (auto from GRN)" readOnly />
                            </ReferenceInput>

                            <StatusSelectInput source="status" isRequired defaultValue="draft" choices={[
                                { id: 'draft', name: 'Draft' },
                                { id: 'approved', name: 'Approved' },
                            ]} />

                            <TextInput source="contact_person" label="Contact Person" />
                        </div>

                        <div className="mt-4">
                            <TextInput source="notes" multiline rows={3} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <ListChecks className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Line Items</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <VendorBillItemsInput />
                    </CardContent>
                </Card>

                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Amounts</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <NumberInput source="items_total" label="Items Total (₹)" readOnly helperText="Auto-summed from line items" />
                            <NumberInput source="tax_amount" label="Tax Amount (₹)" readOnly helperText="Auto-summed from line items" />
                            <NumberInput source="total_amount" label="Total Amount (₹)" readOnly helperText="Items Total + Tax" />
                            <NumberInput source="amount_paid" label="Amount Paid (₹)" defaultValue={0} />
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
